/*
 * <afaq-lightbox> — opens a certificate or honour photo full size in a native
 * <dialog>, used by sections/afaq-credentials.liquid.
 *
 * A custom element for the same reason as <afaq-reel>: the theme editor
 * re-renders sections through innerHTML, which upgrades custom elements but
 * never runs injected scripts, so a gallery added in the editor works at once.
 *
 * <dialog>.showModal() provides the focus trap, Esc to close and the inert
 * background for free. The image is swapped in only when a card is opened, so
 * six full-size certificates never load until someone asks for one.
 */
class AfaqLightbox extends HTMLElement {
  connectedCallback() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) return;
    this.img = this.dialog.querySelector('[data-lightbox-img]');
    this.caption = this.dialog.querySelector('[data-lightbox-caption-target]');

    this.onClick = this.onClick.bind(this);
    this.addEventListener('click', this.onClick);

    // Native <dialog> closes on Esc through its `cancel` event, but that path
    // is not dependable everywhere (a focused <img> or an embedding webview can
    // swallow it). Handle the key directly so Esc always closes.
    this.dialog.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      this.dialog.close();
    });

    this.dialog.addEventListener('close', () => {
      document.documentElement.classList.remove('afaq-lightbox-open');
      this.opener?.focus();
    });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
  }

  onClick(event) {
    if (event.target.closest('[data-lightbox-close]')) {
      this.dialog.close();
      return;
    }

    // A click on the backdrop lands on the <dialog> itself, not its panel.
    if (event.target === this.dialog) {
      this.dialog.close();
      return;
    }

    const card = event.target.closest('[data-lightbox-src]');
    if (!card || !this.contains(card)) return;
    this.open(card);
  }

  open(card) {
    this.opener = card;
    const { lightboxSrc, lightboxAlt, lightboxCaption, lightboxWidth, lightboxHeight } = card.dataset;

    this.img.src = lightboxSrc;
    this.img.alt = lightboxAlt || '';
    if (lightboxWidth && lightboxHeight) {
      this.img.width = lightboxWidth;
      this.img.height = lightboxHeight;
    }
    this.caption.textContent = lightboxCaption || '';
    this.caption.hidden = !lightboxCaption;

    document.documentElement.classList.add('afaq-lightbox-open');
    this.dialog.showModal();
  }
}

if (!customElements.get('afaq-lightbox')) customElements.define('afaq-lightbox', AfaqLightbox);
